// =============================================================================
// FilterSheet.tsx
// Bottom sheet shown when the user taps "Filters" on Dashboard or Inventory.
// Status / Department / Category sections of pill chips, "Clear all" link in
// the header, "Apply filters" button at the bottom. Module-level memoized
// component (extracted in Stage 1 to fix the remount-on-every-tap bug).
// =============================================================================

import React from 'react';

import { Department } from '../../../lib/types';

import { S, ALL_STATUSES, closeSheetAnim } from '../mobileStyles';
import { DateRange, labelForRange } from './DateRangePicker';

// ── TYPES ────────────────────────────────────────────────────────────

interface FilterSheetProps {
  showFilters: boolean;
  setShowFilters: (v: boolean) => void;
  filterStatus: string;
  setFilterStatus: (v: string) => void;
  filterDept: string;
  setFilterDept: (v: string) => void;
  filterCat: string;
  setFilterCat: (v: string) => void;
  departments: Department[];
  categories: string[];
  closingSheet: string | null;
  setClosingSheet: (v: string | null) => void;
  dateRange: DateRange;
  onOpenDateRange: () => void;
  onClearDateRange: () => void;
}

// ── MAIN COMPONENT ───────────────────────────────────────────────────

function FilterSheetImpl({
  showFilters, setShowFilters,
  filterStatus, setFilterStatus,
  filterDept, setFilterDept,
  filterCat, setFilterCat,
  departments, categories,
  closingSheet, setClosingSheet,
  dateRange, onOpenDateRange, onClearDateRange,
}: FilterSheetProps) {
  if (!showFilters) return null;

  // Filter chips inside this sheet act as toggles (status/dept/category
  // selectors), not as compact decorative chips — bumped to button-text size
  // so they read as primary controls, not as metadata.
  const chipPad = { padding: '8px 16px', fontSize: 'var(--m-text-button)', fontWeight: 600 } as const;
  const isDateActive = dateRange.value !== 'all' && (dateRange.from !== '' || dateRange.to !== '');

  return (
    <div
      style={S.sheet}
      className={closingSheet === 'filter' ? 'm-sheet-closing' : 'm-sheet-backdrop'}
      onClick={() => closeSheetAnim(setClosingSheet, setShowFilters, 'filter')}
    >
      <div style={S.sheetContent} className="m-sheet-content" onClick={e => e.stopPropagation()}>
        <div style={S.handle as any} />

        {/* Header */}
        <div style={{
          padding: '8px 20px 12px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderBottom: '0.5px solid var(--border)',
        }}>
          <span style={{ fontSize: 17, fontWeight: 600 }}>Filters</span>
          <button
            onClick={() => { setFilterStatus('all'); setFilterDept('all'); setFilterCat('all'); onClearDateRange(); }}
            style={{ fontSize: 'var(--m-text-meta)', color: 'var(--accent)', background: 'none', border: 'none', fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' }}
          >Clear all</button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px' }}>

          {/* Date range */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 'var(--m-text-label)', fontWeight: 600, color: 'var(--text2)', marginBottom: 8 }}>Date range</div>
            <button
              onClick={onOpenDateRange}
              className={`filter-chip ${isDateActive ? 'is-active' : ''}`}
              style={{ ...chipPad, justifyContent: 'space-between', width: '100%' }}
            >
              <span>{labelForRange(dateRange)}</span>
              <svg viewBox="0 0 12 12" width="10" height="10" fill="currentColor" style={{ opacity: .6 }}>
                <path d="M2 4l4 4 4-4" />
              </svg>
            </button>
          </div>

          {/* Status */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 'var(--m-text-label)', fontWeight: 600, color: 'var(--text2)', marginBottom: 8 }}>Status</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {['all', ...ALL_STATUSES.map(s => s.toLowerCase())].map(s => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`filter-chip ${filterStatus === s ? 'is-active' : ''}`}
                  style={chipPad}
                >
                  {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Department */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 'var(--m-text-label)', fontWeight: 600, color: 'var(--text2)', marginBottom: 8 }}>Department</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <button
                onClick={() => setFilterDept('all')}
                className={`filter-chip ${filterDept === 'all' ? 'is-active' : ''}`}
                style={chipPad}
              >All</button>
              {departments.map(d => (
                <button
                  key={d.id}
                  onClick={() => setFilterDept(d.id)}
                  className={`filter-chip ${filterDept === d.id ? 'is-active' : ''}`}
                  style={chipPad}
                >{d.name}</button>
              ))}
            </div>
          </div>

          {/* Category */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 'var(--m-text-label)', fontWeight: 600, color: 'var(--text2)', marginBottom: 8 }}>Category</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <button
                onClick={() => setFilterCat('all')}
                className={`filter-chip ${filterCat === 'all' ? 'is-active' : ''}`}
                style={chipPad}
              >All</button>
              {categories.map(c => (
                <button
                  key={c}
                  onClick={() => setFilterCat(c)}
                  className={`filter-chip ${filterCat === c ? 'is-active' : ''}`}
                  style={chipPad}
                >{c}</button>
              ))}
            </div>
          </div>

          {/* Apply button */}
          <button
            onClick={() => closeSheetAnim(setClosingSheet, setShowFilters, 'filter')}
            style={S.greenBtn}
          >Apply filters</button>
        </div>
      </div>
    </div>
  );
}

export const FilterSheet = React.memo(FilterSheetImpl);
